import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import { AccommodationPackageOptionTileVisualStyle } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomOptionTile'
import OfferInclusionItem from 'components/Common/Inclusions/OfferInclusionItem'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferPagePackageInclusionsList from 'components/OfferPage/OfferPagePackageInclusionsList'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from 'LuxKit/Typography/BodyText'

export const AnytimeRateOptionTile = styled.div`
  ${AccommodationPackageOptionTileVisualStyle}

  display: grid;
  gap: ${rem(12)} ${rem(32)};
  grid-template-columns: 1fr;
  grid-auto-rows: min-content;

  ${mediaQueryUp.tablet} {
    gap: ${rem(12)} ${rem(16)};
  }

  ${mediaQueryUp.desktop} {
    gap: ${rem(12)} ${rem(32)};
    grid-template-columns: ${rem(238)} 1fr;
  }
`

function isValidInclusion({ minDuration, maxDuration }: App.OfferInclusion, duration: number): boolean {
  return (
    (minDuration == null || duration >= minDuration) &&
    (maxDuration == null || duration <= maxDuration)
  )
}

interface Props {
  propertyRoom: App.Property.Room
  duration: number
}

function PropertyAnytimeRateOption(props: Props) {
  const { propertyRoom, duration } = props

  const intl = useIntl()
  const [inclusions, bonusInclusions, luxPlusInclusions] = useMemo(() => [
    propertyRoom.inclusions.filter(inclusion => isValidInclusion(inclusion, duration)),
    propertyRoom.bonusInclusions.filter(inclusion => isValidInclusion(inclusion, duration)),
    propertyRoom.luxPlusInclusions.filter(inclusion => isValidInclusion(inclusion, duration)),
  ], [propertyRoom, duration])

  if (inclusions.length === 0 && bonusInclusions.length === 0 && luxPlusInclusions.length === 0) return null

  return <AnytimeRateOptionTile>
    <BodyText
      variant="large"
      weight="bold"
      colour="neutral-one"
    >
      {intl.formatMessage({
        id: 'Q81wyQpQgKNK8UxtYyU6',
        defaultMessage: 'Select dates to unlock package options',
        description: 'Placeholder text when dates aren’t selected for property anytime rate option',
      })}
    </BodyText>
    <LargeContentToggle
      maxHeight={208}
      showMoreText={intl.formatMessage({
        id: 'HYsmEAZvwGf6aOMdJgYl',
        defaultMessage: 'View more inclusions',
        description: 'Toggle text to expand inclusions list for property anytime rate option',
      })}
      showLessText={intl.formatMessage({
        id: '3nlGejct7MEq8uv6MIUa',
        defaultMessage: 'View less inclusions',
        description: 'Toggle text to collapse inclusions list for property anytime rate option',
      })}
    >
      <VerticalSpacer gap={12}>
        {luxPlusInclusions.length > 0 && <LuxPlusInclusions
          luxPlusInclusions={luxPlusInclusions}
        />}
        {/* TODO: eslint-disable-next-line formatjs/no-literal-string-in-jsx — behavioural discriminator, not display text */}
        {bonusInclusions.length > 0 && <OfferPagePackageInclusionsList title="bonus">
          {bonusInclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
        </OfferPagePackageInclusionsList>}
        {inclusions.length > 0 && <VerticalSpacer gap={4}>
          {inclusions.map(inclusion => <OfferInclusionItem key={inclusion.id} inclusion={inclusion} />)}
        </VerticalSpacer>}
      </VerticalSpacer>
    </LargeContentToggle>
  </AnytimeRateOptionTile>
}

export default withOfferPageState((state) => ({
  duration: state.duration,
  open: !!state.activeFilter,
}), PropertyAnytimeRateOption, preventRenderWithOpenFilter)
